'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import ProductTile from 'src/components/design-system/ProductTile'
import { Typography, Button } from 'src/components/design-system'
import ArrowSliderHomePrev from 'public/icons/arrows/arrow-circle-left.svg'
import ArrowSliderHomeNext from 'public/icons/arrows/arrow-circle-right.svg'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'

export default function PreviewsClient({ title, previews = [], ctaLabel, ctaHref, variant = '' }) {
  const splideRef = useRef(null)
  const translateEntry = useTranslateEntry()

  const safePreviews = useMemo(() => (Array.isArray(previews) && previews.length ? previews : []), [previews])
  const hasSlides = safePreviews.length > 0
  const [pages, setPages] = useState(() => ({
    current: hasSlides ? 1 : 0,
    total: hasSlides ? Math.max(1, Math.ceil(safePreviews.length / 3)) : 1,
  }))

  useEffect(() => {
    if (!hasSlides) {
      setPages({ current: 0, total: 0 })
    } else {
      setPages((prev) => ({
        current: prev.current || 1,
        total: Math.max(1, prev.total || Math.ceil(safePreviews.length / 3)),
      }))
    }
  }, [hasSlides, safePreviews.length])

  if (!hasSlides) {
    return null
  }

  const updatePages = (splide, nextIndex) => {
    if (!splide) {
      return
    }
    const controller = splide.Components?.Controller
    const slides = splide.length ?? safePreviews.length
    if (!controller || !slides) {
      return
    }
    const lastIndex = Math.max(0, slides - 1)
    const total = Math.max(1, controller.toPage(lastIndex) + 1)
    const activeIndex = typeof nextIndex === 'number' ? nextIndex : typeof splide.index === 'number' ? splide.index : 0
    const current = Math.min(total, controller.toPage(activeIndex) + 1)
    setPages({ current, total })
  }

  const handleMove = (splide, newIndex) => updatePages(splide, newIndex)
  const handleMoved = (splide, newIndex) => updatePages(splide, newIndex)
  const handleMounted = (splide) => updatePages(splide)

  const handlePrev = () => {
    splideRef.current?.go('<')
  }

  const handleNext = () => {
    splideRef.current?.go('>')
  }

  return (
    <section
      className={`mx-auto flex w-full max-w-screen-2xl flex-col px-16 d:px-40 ${variant === 'block' ? 'gap-40 my-64 related-publications overflow-hidden' : 'gap-32 py-32'}`}
    >
      {title && (
        <Typography variant="h2-caps-bold" component="h2">
          {title}
        </Typography>
      )}
      {hasSlides && (
        <Splide
          ref={splideRef}
          className="splide w-full min-w-0"
          hasTrack={false}
          options={{
            mediaQuery: 'min',
            rewind: true,
            pagination: false,
            updateOnMove: true,
            arrows: false,
            easing: 'cubic-bezier(0.49, 0.025, 0.49, 1)',
            padding: { right: '6rem' },
            gap: '1rem',
            breakpoints: {
              768: {
                speed: 500,
                perPage: 2,
                padding: { right: 0 },
              },
              1230: {
                perPage: 3,
              },
              1440: {
                perPage: variant === 'block' ? 4 : 3,
              },
            },
          }}
          onMove={handleMove}
          onMoved={handleMoved}
          onMounted={handleMounted}
          onResized={handleMounted}
        >
          <SplideTrack className="splide__track">
            {safePreviews.map((preview) => (
              <SplideSlide key={preview.id}>
                <ProductTile {...preview} />
              </SplideSlide>
            ))}
          </SplideTrack>
        </Splide>
      )}

      <div
        className={`flex flex-wrap items-center gap-32 ${variant === 'block' ? 'max-w-[653px] relative mx-auto w-full' : ''}`}
      >
        {ctaHref && ctaLabel && (
          <Button size="l" variant="light" icon="left" href={ctaHref}>
            {ctaLabel}
          </Button>
        )}

        {hasSlides && (
          <div className="flex items-center gap-x-32 flex-1 rounded-full hover:border hover:border-basic-white/100 border-basic-white/0 transition-border duration-300">
            <div className="flex items-center gap-8">
              <button
                className="cursor-pointer rounded-full border-transparent border hover:border-current transition-colors duration-300"
                onClick={handlePrev}
                aria-label={translateEntry('T_SLIDER_PREV')}
                type="button"
              >
                <ArrowSliderHomePrev />
              </button>
              <span className="font-serif text-buttons-l leading-111">
                {pages.current || 0}/{pages.total || 0}
              </span>
            </div>

            <div className="relative h-12 min-w-112 w-full overflow-hidden border-b border-b-dark-blue-dark-blue4">
              <div
                className="absolute inset-y-0 left-0 bg-style1 transition-[width] duration-500"
                style={{ width: `${pages.total ? Math.min(100, (pages.current / pages.total) * 100) : 0}%` }}
              />
            </div>

            <button
              className="cursor-pointer rounded-full border-transparent border hover:border-current transition-colors duration-300"
              onClick={handleNext}
              aria-label={translateEntry('T_SLIDER_NEXT')}
              type="button"
            >
              <ArrowSliderHomeNext />
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
